/**
 * Register the service worker in production-ish environments. Skips in
 * localhost dev to avoid stale caches frustrating you while iterating.
 */
export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  if (window.location.hostname === "localhost") return;
  window.addEventListener("load", () => {
    // Versioned URL busts CDN-cached copies of the old worker. Bumped
    // to v18 in iter279 so every iPhone/Android still holding the v14
    // SW re-downloads the v18-cache SW and self-invalidates — otherwise
    // the old v14 SW keeps intercepting `/f/` scan pages with stale
    // HTML. `updateViaCache: 'none'` forces every future update check
    // to hit the network for sw.js instead of the browser's HTTP cache.
    navigator.serviceWorker.register("/sw.js?v=19", { updateViaCache: "none" }).catch(() => {
      // Silent — SW is a progressive enhancement, not a hard requirement.
    });
    // Also trigger an update check on every load in case the browser
    // has already installed our SW previously — this asks the SW
    // manager to re-fetch sw.js right now instead of waiting for the
    // browser's own 24h check.
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (reg) reg.update().catch(() => { /* silent */ });
    }).catch(() => { /* silent */ });
    // When the new SW activates it broadcasts { type: "sw:activated" }.
    // Reload once so the tab actually runs the new bundle rather than
    // waiting for the user to hard-refresh. Guard against reload loops
    // with a sessionStorage flag scoped to the activated cache name.
    navigator.serviceWorker.addEventListener("message", (event) => {
      const m = event?.data;
      if (m?.type !== "sw:activated" || !m.cache) return;
      try {
        const key = "__sw_reloaded_" + m.cache;
        if (window.sessionStorage.getItem(key)) return;
        window.sessionStorage.setItem(key, "1");
        window.location.reload();
      } catch (_e) {
        window.location.reload();
      }
    });
  });
}

/**
 * Returns true when this page is running as an installed PWA. Used to
 * hide the "Install app" button after the user has installed.
 */
export function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

/* ───────────────────────────────────────────────────────────────────────
   Phase 25 — Web Push helpers
   ─────────────────────────────────────────────────────────────────────── */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

/** True when the browser supports the Web Push API and is over HTTPS. */
export function pushSupported() {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * Subscribe the current browser to Web Push notifications for this tenant.
 * Returns the subscription object or throws.
 *
 * Caller is responsible for hitting POST /api/push/subscribe with the
 * returned subscription's `endpoint` and `keys`.
 */
export async function subscribeToPush({ apiBase, fetchKey }) {
  if (!pushSupported()) throw new Error("push_not_supported");
  const reg = await navigator.serviceWorker.ready;
  // Re-use any existing subscription
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const { public_key } = await (fetchKey
      ? fetchKey()
      : fetch(`${apiBase || ""}/api/push/key`).then((r) => r.json()));
    if (!public_key) throw new Error("vapid_key_missing");
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key),
    });
  }
  return sub;
}

export async function unsubscribeFromPush() {
  if (!pushSupported()) return false;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await sub.unsubscribe();
    return sub.endpoint;
  }
  return null;
}
